import type { BaseEventListenerProps } from '../AbstractEventListener';
import { AbstractEventListener } from '../AbstractEventListener';
import type { BasePermission, EmptyObject, LoggedEvent } from '../loggingTypes';
import type { TestCustomEvent, TestLoggingContext } from './testLogEvents';

export interface TestEventListenerProps<
  LoggingContext extends EmptyObject = EmptyObject,
  CustomEvents extends EmptyObject = EmptyObject,
  LoggingPermission extends BasePermission = BasePermission
> extends BaseEventListenerProps<LoggingContext, CustomEvents> {
  requiredPermissions: Array<keyof LoggingPermission>;
}

/**
 * Event listener for testing.
 *
 * All this does is capture any events it would record in a separate array that is publicly
 * accessible.
 */
export class TestEventListener<
  LoggingContext extends TestLoggingContext = TestLoggingContext,
  CustomEvents extends TestCustomEvent = TestCustomEvent,
  LoggingPermission extends BasePermission = BasePermission
> extends AbstractEventListener<LoggingContext, CustomEvents, LoggingPermission> {
  public readonly queuedEvents: Array<Parameters<typeof this.logEvent>> = [];
  public readonly capturedEvents: Array<Parameters<typeof this.logEvent>> = [];
  public readonly permissions = new Set<keyof LoggingPermission>();

  public hasInitalized = true;

  constructor(props?: TestEventListenerProps<LoggingContext, CustomEvents, LoggingPermission>) {
    super({
      requiredPermissions: props?.requiredPermissions ?? [],
      filter: props?.filter,
      useAsync: props?.useAsync ?? false,
      asyncTimeoutMs: props?.asyncTimeoutMs,
    });
  }

  /** @override */
  protected logEventInternal(
    event: LoggedEvent<CustomEvents>,
    context: Partial<LoggingContext>
  ): void {
    this.capturedEvents.push([event, context]);
  }

  /** Resets the state of the listener. */
  public reset = (): void => {
    this.queuedEvents.length = 0;
    this.capturedEvents.length = 0;
    this.permissions.clear();
    this.hasInitalized = false;
  };

  /** @override */
  protected flushInternal: () => Promise<unknown> = () => Promise.resolve();
}
